import moment from 'moment';

/**
 * Placing instants on a time axis, and labelling it.
 *
 * Pure arithmetic over a window, with no notion of what is drawn on it — so any timeline can
 * share it with any other.
 */
/** The window the timeline draws, and the listing asks the engine about. */
export type TimeRange = {
    from: Date;
    to: Date;
};

export type AxisTick = {
    at: Date;
    label: string;
};

/** Where an instant sits across the window, as 0–1. Clamped, so nothing draws off the axis. */
export const fractionAlong = (at: Date, range: TimeRange): number => {
    const span = range.to.getTime() - range.from.getTime();
    if (span <= 0) {
        return 0;
    }
    return Math.min(1, Math.max(0, (at.getTime() - range.from.getTime()) / span));
};

type TickUnit = {
    unit: moment.unitOfTime.StartOf;
    step: number;
    format: string;
};

/**
 * The coarsest unit that still gives a readable number of ticks. Chosen by span rather than
 * by a fixed count, so the labels read as dates a person recognises — "21 Sep", "Mar 2026",
 * "2022" — instead of arbitrary instants.
 */
const TICK_UNITS: { maxDays: number; tick: TickUnit }[] = [
    { maxDays: 3, tick: { unit: 'hour', step: 6, format: 'HH:mm' } },
    { maxDays: 14, tick: { unit: 'day', step: 1, format: 'D MMM' } },
    { maxDays: 90, tick: { unit: 'day', step: 7, format: 'D MMM' } },
    { maxDays: 400, tick: { unit: 'month', step: 1, format: 'MMM YYYY' } },
    { maxDays: 1500, tick: { unit: 'month', step: 3, format: 'MMM YYYY' } },
    { maxDays: Number.POSITIVE_INFINITY, tick: { unit: 'year', step: 1, format: 'YYYY' } },
];

export const axisTicksFor = (range: TimeRange): AxisTick[] => {
    const spanDays = moment.utc(range.to).diff(moment.utc(range.from), 'days', true);
    if (spanDays <= 0) {
        return [{ at: range.from, label: moment.utc(range.from).format('D MMM YYYY') }];
    }

    const { tick } = TICK_UNITS.find(candidate => spanDays <= candidate.maxDays) ?? TICK_UNITS[TICK_UNITS.length - 1];

    const ticks: AxisTick[] = [];
    // Start at the first unit boundary at or after the window opens, so labels land on whole
    // days and months rather than on whatever instant the window happens to begin at.
    const cursor = moment.utc(range.from).startOf(tick.unit);
    if (cursor.isBefore(moment.utc(range.from))) {
        cursor.add(tick.step, tick.unit as moment.unitOfTime.DurationConstructor);
    }

    const end = moment.utc(range.to);
    // A guard, not a limit: the unit above is chosen so this is never the thing that stops it.
    while (cursor.isSameOrBefore(end) && ticks.length < 24) {
        ticks.push({ at: cursor.toDate(), label: cursor.format(tick.format) });
        cursor.add(tick.step, tick.unit as moment.unitOfTime.DurationConstructor);
    }

    return ticks;
};
